/* eslint-disable react/no-unescaped-entities */
/**
 * Beta enrollment form connected to the unified public endpoint
 * ``POST /api/beta/enroll``. Prospects land in the ``beta_requests``
 * collection, visible to staff at ``GET /api/beta/requests`` and from
 * the platform-admin UI. Only staff can then promote a prospect to a
 * full user via ``POST /api/beta/grant``.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
       from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { BRAND } from "@/config/brand";

const OWNERSHIP = [
  { value: "personal", label: "En nom propre" },
  { value: "sci", label: "En SCI" },
  { value: "mixed", label: "Mixte" },
];

const NEEDS = [
  { value: "gestion_locative", label: "Gestion locative et loyers" },
  { value: "sci_holdings", label: "Gestion multi-SCI ou holdings" },
  { value: "rentabilite", label: "Suivi de rentabilité et cash-flow" },
  { value: "documents", label: "Centralisation des documents" },
  { value: "tenant_portal", label: "Espace locataire" },
  { value: "comptabilite", label: "Préparation comptable" },
];

export default function BetaFormDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    portfolio_size: "",
    ownership: "personal",
    main_need: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.consent) {
      toast.error("Renseignez tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      // Uses the existing beta enrollment endpoint — single source of truth
      // for the beta programme (no parallel prospect base).
      await api.post("/beta/enroll", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        portfolio_size: form.portfolio_size ? Number(form.portfolio_size) : null,
        ownership: form.ownership,
        main_need: form.main_need || null,
        consent_recontact: true,
        source: "landing_public",
      });
      toast.success(
        "Merci ! Votre demande a bien été reçue. Nous revenons vers vous très vite."
      );
      onOpenChange(false);
      setForm({
        first_name: "", last_name: "", email: "", portfolio_size: "",
        ownership: "personal", main_need: "", consent: false,
      });
    } catch (err) {
      toast.error(
        "Impossible d'envoyer la demande. Merci de réessayer dans un instant."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="fonbex-beta-dialog">
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-semibold"
            style={{ color: BRAND.colors.night }}
          >
            Demander un accès bêta {BRAND.name}
          </DialogTitle>
          <DialogDescription>
            Programme fermé, gratuit pendant la phase bêta, avec accompagnement
            personnalisé.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first_name">Prénom *</Label>
              <Input id="first_name" data-testid="fonbex-beta-first-name"
                     required value={form.first_name} onChange={set("first_name")} />
            </div>
            <div>
              <Label htmlFor="last_name">Nom *</Label>
              <Input id="last_name" data-testid="fonbex-beta-last-name"
                     required value={form.last_name} onChange={set("last_name")} />
            </div>
          </div>
          <div>
            <Label htmlFor="email">Adresse e-mail *</Label>
            <Input id="email" type="email" data-testid="fonbex-beta-email"
                   required value={form.email} onChange={set("email")}
                   placeholder="prenom.nom@exemple.fr" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="portfolio_size">Nombre de biens / lots</Label>
              <Input id="portfolio_size" type="number" min="0"
                     data-testid="fonbex-beta-portfolio-size"
                     value={form.portfolio_size} onChange={set("portfolio_size")} />
            </div>
            <div>
              <Label>Détention</Label>
              <Select value={form.ownership} onValueChange={set("ownership")}>
                <SelectTrigger data-testid="fonbex-beta-ownership">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OWNERSHIP.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Besoin principal</Label>
            <Select value={form.main_need} onValueChange={set("main_need")}>
              <SelectTrigger data-testid="fonbex-beta-main-need">
                <SelectValue placeholder="Sélectionnez votre besoin prioritaire…" />
              </SelectTrigger>
              <SelectContent>
                {NEEDS.map((n) => (
                  <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox
              data-testid="fonbex-beta-consent"
              checked={form.consent}
              onCheckedChange={(v) => set("consent")(!!v)}
            />
            <span>
              J'accepte que {BRAND.name} me recontacte pour la phase bêta. Aucun
              partage à des tiers.
            </span>
          </label>
          <DialogFooter className="pt-2">
            <Button
              type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="fonbex-beta-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit" disabled={submitting}
              data-testid="fonbex-beta-submit"
              style={{
                backgroundColor: BRAND.colors.primary,
                color: BRAND.colors.white,
              }}
            >
              {submitting ? "Envoi…" : "Envoyer la demande"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
